/**
 * Meta Pixel — loads fbevents.js and reports page views.
 *
 * The id comes from `VITE_META_PIXEL_ID`. Without it nothing is loaded: no script, no cookie,
 * no request. That keeps local development and preview builds out of the production pixel.
 *
 * **If you change the id, change it in `.gitlab-ci.yml` too.** That job writes `.env.production`
 * line by line, so a variable missing there simply does not exist in the deployed build — and a
 * pixel that never initializes looks exactly like one that is working but has no traffic.
 *
 * This app is a SPA: `init` + the first `PageView` only cover the landing screen, so
 * `trackPixelPageView` is called on every route change, next to the GA4 one in `AppRouter`.
 */

import { env } from "@services/config/env";

/** The global Meta installs, plus the queue it uses before its script loads. */
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: unknown;
  loaded: boolean;
  version: string;
};

type WindowWithPixel = Window & { fbq?: Fbq; _fbq?: Fbq };

let started = false;
/** The router reports the landing route too; the first of those two is dropped. */
let initialViewConsumed = false;

/** Install the pixel and report the first page view. Safe to call more than once. */
export function initMetaPixel(): void {
  if (!env.metaPixelId) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  // A second call would report the same visitor twice.
  if (started) return;
  started = true;

  const w = window as WindowWithPixel;

  if (!w.fbq) {
    // Calls made before fbevents.js loads are queued, then replayed through callMethod.
    const fbq = ((...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    }) as Fbq;
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    w.fbq = fbq;
    w._fbq ??= fbq;

    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }

  w.fbq("init", env.metaPixelId);
  w.fbq("track", "PageView");
}

/**
 * Report a page view on a route change.
 *
 * **Skips the first call on purpose.** `initMetaPixel` already reports the landing screen, and the
 * router's effect runs right after mount for that same route — without this, every visit would be
 * counted twice and the inflated number looks like real traffic.
 */
export function trackPixelPageView(): void {
  if (!started) return;
  if (!initialViewConsumed) {
    initialViewConsumed = true;
    return;
  }
  const w = window as WindowWithPixel;
  w.fbq?.("track", "PageView");
}
